import { supabase } from '../lib/supabase'
import { supabaseError } from '../lib/errors'
import type { InboxFeedItem, InboxItemType } from '../types/inbox'
import type { PhoneCallLog } from './phoneCallLogs'

export interface ConversationRow {
  id: string
  channel: 'whatsapp' | 'sms' | 'email'
  contact_name: string
  contact_info: string
  last_message: string | null
  last_message_at: string | null
  unread_count: number
  customer_id: string | null
  property_id?: string | null
  contract_id?: string | null
  job_id?: string | null
  employee_id?: string | null
  lead_id?: string | null
  proposal_id?: string | null
  mailbox_id?: string | null
  received_address?: string | null
  state?: string | null
  subject?: string | null
  assigned_to_user_id?: string | null
  assigned_at?: string | null
  handling_status?: string | null
  waiting_until?: string | null
  priority?: string | null
  queue_id?: string | null
  sla_due_at?: string | null
  created_at: string
  internal_notes?: string | null
}

function mapConversation(row: ConversationRow): InboxFeedItem {
  return {
    itemType: 'conversation',
    id: row.id,
    sortAt: row.last_message_at || row.created_at,
    channel: row.channel,
    contactName: row.contact_name || row.contact_info || 'Unknown',
    contactInfo: row.contact_info,
    preview: row.last_message || 'No messages yet',
    subject: row.subject,
    unreadCount: row.unread_count || 0,
    state: row.state || 'active',
    handlingStatus: (row.handling_status as InboxFeedItem['handlingStatus']) || 'open',
    waitingUntil: row.waiting_until || null,
    assignedToUserId: row.assigned_to_user_id || null,
    assignedAt: row.assigned_at || null,
    priority: (row.priority as InboxFeedItem['priority']) || 'normal',
    queueId: row.queue_id || null,
    slaDueAt: row.sla_due_at || null,
    customerId: row.customer_id,
    propertyId: row.property_id || null,
    contractId: row.contract_id || null,
    jobId: row.job_id || null,
    leadId: row.lead_id || null,
    proposalId: row.proposal_id || null,
    employeeId: row.employee_id || null,
    mailboxId: row.mailbox_id || null,
    receivedAddress: row.received_address || null,
    staffNotes: row.internal_notes?.trim() || null,
  }
}

function mapPhoneCall(row: PhoneCallLog & {
  assigned_to_user_id?: string | null
  assigned_at?: string | null
  handling_status?: string | null
  waiting_until?: string | null
  priority?: string | null
  queue_id?: string | null
  sla_due_at?: string | null
  state?: string | null
  lead_id?: string | null
  proposal_id?: string | null
}): InboxFeedItem {
  const preview = row.direction === 'incoming' ? 'Incoming call' : 'Outbound call'
  return {
    itemType: 'phone_call_log',
    id: row.id,
    sortAt: row.created_at,
    channel: 'call',
    contactName: row.caller_name || row.phone_number || 'Unknown',
    contactInfo: row.phone_number,
    preview,
    unreadCount: 0,
    state: row.state || 'active',
    handlingStatus: (row.handling_status as InboxFeedItem['handlingStatus']) || 'open',
    waitingUntil: row.waiting_until || null,
    assignedToUserId: row.assigned_to_user_id || null,
    assignedAt: row.assigned_at || null,
    priority: (row.priority as InboxFeedItem['priority']) || 'normal',
    queueId: row.queue_id || null,
    slaDueAt: row.sla_due_at || null,
    customerId: row.customer_id,
    propertyId: row.property_id || null,
    contractId: row.contract_id || null,
    jobId: row.job_id || null,
    leadId: row.lead_id || null,
    proposalId: row.proposal_id || null,
    employeeId: null,
    direction: row.direction,
    durationSeconds: row.duration_seconds,
    staffNotes: row.notes?.trim() || null,
  }
}

export async function fetchUnifiedInboxFeed(): Promise<InboxFeedItem[]> {
  const [convRes, callRes] = await Promise.all([
    supabase.from('conversations').select('*').order('last_message_at', { ascending: false }),
    supabase
      .from('phone_call_logs')
      .select(
        'id, call_id, phone_number, direction, duration_seconds, status, caller_name, line_number, notes, customer_id, property_id, contract_id, job_id, lead_id, proposal_id, source, created_by, created_at, updated_at, assigned_to_user_id, assigned_at, handling_status, waiting_until, priority, queue_id, sla_due_at, state'
      )
      .order('created_at', { ascending: false })
      .limit(300),
  ])

  if (convRes.error) throw supabaseError(convRes.error, 'Failed to load inbox conversations')
  if (callRes.error) throw supabaseError(callRes.error, 'Failed to load call logs')

  const items: InboxFeedItem[] = [
    ...((convRes.data || []) as ConversationRow[]).map(mapConversation),
    ...((callRes.data || []) as PhoneCallLog[]).map(mapPhoneCall),
  ]

  items.sort((a, b) => b.sortAt.localeCompare(a.sortAt))
  return items
}

export function inboxItemKey(itemType: InboxItemType, id: string): string {
  return `${itemType}:${id}`
}

export function parseInboxItemKey(key: string): { itemType: InboxItemType; id: string } | null {
  const idx = key.indexOf(':')
  if (idx <= 0) return null
  const itemType = key.slice(0, idx) as InboxItemType
  if (itemType !== 'conversation' && itemType !== 'phone_call_log') return null
  return { itemType, id: key.slice(idx + 1) }
}

export async function fetchInboxDashboardCounts(userId: string | null): Promise<{
  staffUnassigned: number
  overdueTasks: number
  myOpenTasks: number
  needsResponse: number
}> {
  const [unassignedRes, tasksRes] = await Promise.all([
    supabase
      .from('conversations')
      .select('id', { count: 'exact', head: true })
      .eq('handling_status', 'open')
      .is('assigned_to_user_id', null)
      .eq('state', 'active'),
    supabase
      .from('inbox_tasks')
      .select('id, assignee_id, due_at, status, snoozed_until')
      .in('status', ['open', 'in_progress', 'blocked', 'snoozed']),
  ])

  const now = new Date()
  const tasks = tasksRes.data || []
  let overdueTasks = 0
  let myOpenTasks = 0
  for (const t of tasks) {
    if (userId && t.assignee_id === userId) myOpenTasks++
    if (t.due_at && new Date(t.due_at) < now && t.status !== 'done') overdueTasks++
  }

  const slaRes = await supabase
    .from('conversations')
    .select('id', { count: 'exact', head: true })
    .eq('state', 'active')
    .in('handling_status', ['open', 'assigned'])
    .lt('sla_due_at', now.toISOString())

  return {
    staffUnassigned: unassignedRes.count || 0,
    overdueTasks,
    myOpenTasks,
    needsResponse: slaRes.count || 0,
  }
}
